import { FC } from 'react';
import { z } from 'zod';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { Button, Stack } from '@mui/material';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { OpportunityFilters, statusOptions, useOpportunityFilters } from './useOpportunityFilters';
import { ControlledFormField } from '../../form/ControlledFormField';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { DateRangePicker } from '../../form/DateRangePicker';
import { TagsCheckList } from '../../form/TagsCheckList';
import { OwnersCheckList } from '../../form/OwnersCheckList';
import { StatusCheckList } from '../../form/StatusCheckList';
import { JourneySelect } from '../../form/JourneySelect';
import { BenefitCheckList } from '../../form/BenefitCheckList';

const opportunityFilterDrawerSchema = z.object({});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		journeyId: z.string(),
		date: z
			.tuple([
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
			])
			.nullable(),
		benefit: z.array(z.string()),
		tags: z.array(z.string()),
		owners: z.array(z.string()),
		status: z.array(z.enum(statusOptions)),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const dateRangeToTuple = (dateRange: OpportunityFilters['date']): [string, string] | undefined => {
	if (dateRange == null) {
		return undefined;
	}

	return [dateRange.start, dateRange.end];
};

const dateRangeFromTuple = (dateTuple: [string | null, string | null] | null): OpportunityFilters['date'] => {
	if (dateTuple?.[0] == null || dateTuple[1] == null) {
		return undefined;
	}

	return { start: dateTuple[0], end: dateTuple[1] };
};

const OpportunityFilterDrawer: FC = () => {
	const [filters, setFilters] = useOpportunityFilters();
	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			journeyId: filters.journeyId ?? '',
			date: dateRangeToTuple(filters.date) || null,
			benefit: filters.benefit ?? [],
			tags: filters.tags ?? [],
			owners: filters.owners ?? [],
			status: filters.status ?? [],
		},
	});

	const onSubmit = (values: FormValues) => {
		setFilters({
			journeyId: values.journeyId ? values.journeyId : undefined,
			date: dateRangeFromTuple(values.date),
			benefit: values.benefit.length > 0 ? values.benefit : undefined,
			tags: values.tags.length > 0 ? values.tags : undefined,
			owners: values.owners.length > 0 ? values.owners : undefined,
			status: values.status.length > 0 ? values.status : undefined,
		});
	};

	const onClearAll = () => {
		setFilters({});
		form.reset({ journeyId: '', date: null, benefit: [], tags: [], owners: [], status: [] });
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)} style={{ height: '100%' }}>
			<DrawerWrapper height={'100%'}>
				<DrawerHeader title={<FormattedMessage id={'drawer.opportunity-filter.title'} />} />
				<Stack width={'100%'} gap={1.5} height={'100%'} justifyContent={'space-between'}>
					<ControlledFormField
						form={form}
						name={'journeyId'}
						label={intl.formatMessage({ id: 'form.action-filter.date.label.journey' })}
						render={({ id, name, value, onChange }) => <JourneySelect id={id} name={name} value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'date'}
						label={intl.formatMessage({ id: 'form.opportunity-filter.date.label.date' })}
						render={({ id, name, value, onChange }) => <DateRangePicker id={id} name={name} value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'benefit'}
						label={intl.formatMessage({ id: 'form.opportunity-filter.date.label.benefit' })}
						render={({ value, onChange }) => <BenefitCheckList value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'status'}
						label={intl.formatMessage({ id: 'form.opportunity-filter.date.label.status' })}
						render={({ value, onChange }) => <StatusCheckList value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'tags'}
						label={intl.formatMessage({ id: 'form.opportunity-filter.date.label.tags' })}
						render={({ value, onChange }) => <TagsCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<ControlledFormField
						form={form}
						name={'owners'}
						label={intl.formatMessage({ id: 'form.opportunity-filter.date.label.owner' })}
						render={({ value, onChange }) => <OwnersCheckList value={value} onChange={onChange} size={'medium'} />}
					/>

					<Stack flexGrow={1} direction={'row'} justifyContent={'end'} alignItems={'end'} gap={3}>
						<Button variant={'text'} onClick={onClearAll} color={'secondary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.opportunity-filter.clear-all'} />
						</Button>
						<Button variant={'contained'} type={'submit'} color={'primary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.opportunity-filter.apply'} />
						</Button>
					</Stack>
				</Stack>
			</DrawerWrapper>
		</form>
	);
};

export { opportunityFilterDrawerSchema, OpportunityFilterDrawer };
